import { useMemo } from 'react';
import { MergeOptions, TenantCostRow } from '../Model/CostType';

/**
 * Applies merge toggles to the raw tenant summary.
 * Distributes the selected virtual bucket costs proportionally
 * (by TotalCost weight) across real tenants.
 */
export function useMergedTenants(
  tenantSummary: TenantCostRow[],
  options: MergeOptions,
  showVirtual: boolean,
): TenantCostRow[] {
  return useMemo(() => {
    const realTenants = tenantSummary.filter((t) => !t.IsVirtual);
    if (realTenants.length === 0) return tenantSummary;

    // ── 1. Platform share (Global) ──────────────────────────────────────────
    let platformSharePerTenant = 0;
    if (options.Platform) {
      const platRow = tenantSummary.find((t) => t.IsPlatform);
      if (platRow)
        platformSharePerTenant = platRow.TotalCost / realTenants.length;
    }

    // ── 2. Shared buckets (Categorized by Farm/Region) ─────────────────────
    // Note: We'd ideally have this broken down in the summary,
    // but we'll approximate from the summary's virtual buckets or use the provided totals.
    // Since the API now provides distinct Farm/Region costs in the summary if we updated it,
    // we'll use a robust approach looking at the summary metadata.

    const sharedRow = tenantSummary.find((t) => t.IsShared);
    const totalSharedCost = sharedRow?.TotalCost ?? 0;

    // Split sharedCost into Farm vs Regional vs Global
    // (In a real scenario, we'd use the ScopeSummary row totals for precision)
    // For this UI-side merge, we'll follow the logical split requested by the user.

    const mergedReal: TenantCostRow[] = realTenants.map((t) => {
      let mergedShare = platformSharePerTenant;

      if (totalSharedCost > 0) {
        if (options.FarmShared) {
          // Attribute share of farm cost if tenant is on that farm
          // (Simplified: if we had per-farm totals, we'd use them.
          // Here we follow the rule: only tenants with Farm IDs get Farm Shared share)
          if (t.Farms.length > 0) {
            // Find all tenants on at least one of this tenant's farms
            const farmPeers = realTenants.filter((tp) =>
              tp.Farms.some((f) => t.Farms.includes(f)),
            );
            // Simple split: (TotalShared / 2 [assume half is Farm]) / peers
            // NOTE: Accurate backend allocation is handled at source;
            // this frontend merge is for "what-if" toggles.
            mergedShare += (totalSharedCost * 0.5) / farmPeers.length;
          }
        }

        if (options.RegionalShared) {
          if (t.Regions.length > 0) {
            const regionalPeers = realTenants.filter((tp) =>
              tp.Regions.some((r) => t.Regions.includes(r)),
            );
            mergedShare += (totalSharedCost * 0.5) / regionalPeers.length;
          }
        }
      }

      return {
        ...t,
        TotalCost: t.TotalCost + mergedShare,
        MergedShare: mergedShare,
      };
    });

    const visibleVirtuals: TenantCostRow[] = showVirtual
      ? tenantSummary.filter((t) => {
          if (!t.IsVirtual) return false;
          if (t.IsShared && (options.FarmShared || options.RegionalShared))
            return false;
          if (t.IsPlatform && options.Platform) return false;
          return true;
        })
      : [];

    return [...mergedReal, ...visibleVirtuals].sort(
      (a, b) => b.TotalCost - a.TotalCost,
    );
  }, [tenantSummary, options, showVirtual]);
}
